/**
 * BettingPoolEntry Component
 * Allows users to bet star coins FOR or AGAINST AI predictions
 * Integrates with GameMarketView to show betting options on games
 */

import React, { useState, useEffect } from 'react';
import { auth, db, functions } from '../../../firebase';
import { httpsCallable, httpsCallableFromURL } from 'firebase/functions';
import { doc, onSnapshot } from 'firebase/firestore';
import { AlertCircle } from 'lucide-react';
import './BettingPoolEntry.css';

const BettingPoolEntry = ({ game, aiPrediction, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [userBalance, setUserBalance] = useState(0);
  const [betAmount, setBetAmount] = useState('');
  const [betSide, setBetSide] = useState(null); // 'for' or 'against'
  const [poolData, setPoolData] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [bettingClosed, setBettingClosed] = useState(false);
  const [gameStartTime, setGameStartTime] = useState(null);

  // Get user's star coins balance
  useEffect(() => {
    if (!auth.currentUser) return;

    const unsubscribe = onSnapshot(
      doc(db, 'users', auth.currentUser.uid),
      (doc) => {
        if (doc.exists()) {
          const data = doc.data();
          const balance = data.starCoins?.balance || 0;
          setUserBalance(balance);
        }
      }
    );

    return () => unsubscribe();
  }, []);

  // Check if game has started (betting should be closed)
  useEffect(() => {
    if (game.gameTimestamp) {
      const startTime = new Date(game.gameTimestamp);
      const now = new Date();

      setGameStartTime(startTime);

      if (now >= startTime) {
        setBettingClosed(true);
        setError(`Betting closed. Game started at ${startTime.toLocaleString("en-US", {
          timeZone: "America/New_York",
          hour12: true,
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit'
        })} ET`);
      }

      // Update every minute to check if game has started
      const interval = setInterval(() => {
        const currentTime = new Date();
        if (currentTime >= startTime) {
          setBettingClosed(true);
          setError(`Betting closed. Game started at ${startTime.toLocaleString("en-US", {
            timeZone: "America/New_York",
            hour12: true,
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit'
          })} ET`);
        }
      }, 60000); // Check every minute

      return () => clearInterval(interval);
    }
  }, [game.gameTimestamp]);

  // Get current pool data
  useEffect(() => {
    if (!game.gameId) return;

    const poolId = `football_${game.gameId}`;
    const unsubscribe = onSnapshot(
      doc(db, 'bettingPools', poolId),
      (doc) => {
        if (doc.exists()) {
          setPoolData(doc.data());
        } else {
          // Pool doesn't exist yet
          setPoolData({
            homePot: 0,
            awayPot: 0,
            totalBettors: 0,
            status: 'open'
          });
        }
      }
    );

    return () => unsubscribe();
  }, [game.gameId]);

  // Calculate potential payout
  const calculatePotentialPayout = () => {
    if (!betAmount || !betSide || !poolData) return 0;

    const amount = parseFloat(betAmount);
    if (isNaN(amount) || amount <= 0) return 0;

    // Determine which team we're betting on
    const bettingFor = betSide === 'for' ? aiPrediction.consensus : getOppositeTeam();
    const isHome = bettingFor === game.home;

    // Get current pools
    const currentHomePot = poolData.homePot || 0;
    const currentAwayPot = poolData.awayPot || 0;

    // Calculate new pot totals AFTER our bet
    const newHomePot = currentHomePot + (isHome ? amount : 0);
    const newAwayPot = currentAwayPot + (!isHome ? amount : 0);
    const newTotalPot = newHomePot + newAwayPot;

    // Handle edge cases
    // If this is the first bet or only bet on one side
    if (currentHomePot === 0 && currentAwayPot === 0) {
      // First bet in pool - you'd get everything back if you win
      return amount;
    }

    // If betting on empty side against existing bets
    const opposingSidePot = isHome ? currentAwayPot : currentHomePot;
    if ((isHome && currentHomePot === 0) || (!isHome && currentAwayPot === 0)) {
      // You'd win the entire opposing pot plus your bet
      return amount + opposingSidePot;
    }

    // Normal parimutuel calculation
    const ourSidePot = isHome ? newHomePot : newAwayPot;

    // Our share of the total pot = (our bet / our side's total) * total pot
    const payout = Math.floor((amount / ourSidePot) * newTotalPot);

    return payout;
  };

  const getOppositeTeam = () => {
    if (aiPrediction.consensus === game.home) return game.away;
    return game.home;
  };

  const getOdds = () => {
    const payout = calculatePotentialPayout();
    const amount = parseFloat(betAmount) || 0;
    if (amount === 0) return '0:1';

    const ratio = (payout / amount).toFixed(2);
    return `${ratio}:1`;
  };

  const handlePlaceBet = async () => {
    if (!auth.currentUser) {
      setError('Please sign in to place bets');
      return;
    }

    if (!betAmount || !betSide) {
      setError('Please select a side and enter an amount');
      return;
    }

    const amount = parseFloat(betAmount);
    if (isNaN(amount) || amount <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    if (amount > userBalance) {
      setError(`Insufficient balance. You have ${userBalance} star coins`);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Determine which team to bet on
      const bettingFor = betSide === 'for' ? aiPrediction.consensus : getOppositeTeam();
      const team = bettingFor === game.home ? 'home' : 'away';

      // Call the cloud function - v2 functions need full URL
      // Try with standard callable first, fallback to URL if needed
      const placeBet = httpsCallable(functions, 'placeBet');
      const result = await placeBet({
        gameId: `football_${game.gameId}`,
        team: team,
        amount: amount,
        sport: 'football',
        homeTeam: game.home,
        awayTeam: game.away
      });

      if (result.data.success) {
        setSuccess(true);
        setBetAmount('');
        setBetSide(null);

        // Close after 2 seconds
        setTimeout(() => {
          if (onClose) onClose();
        }, 2000);
      }
    } catch (error) {
      console.error('Error placing bet:', error);
      setError(error.message || 'Failed to place bet');
    } finally {
      setLoading(false);
    }
  };

  // Quick bet amounts
  const quickAmounts = [10, 25, 50, 100, 250];

  return (
    <div className="betting-pool-entry">
      <div className="pool-header">
        <h3>PLACE BET</h3>
        <button className="close-btn" onClick={onClose}>×</button>
      </div>

      {/* Game Info */}
      <div className="game-info">
        <div className="teams">
          <span className={`team ${aiPrediction.consensus === game.home ? 'predicted' : ''}`}>
            {game.home}
          </span>
          <span className="vs">vs</span>
          <span className={`team ${aiPrediction.consensus === game.away ? 'predicted' : ''}`}>
            {game.away}
          </span>
        </div>
        <div className="ai-prediction">
          <span className="label">AI PICK</span>
          <span className="winner">{aiPrediction.consensus || 'TBD'}</span>
          <span className="confidence">
            {(() => {
              const conf = parseFloat(aiPrediction.consensusConfidence);
              return isNaN(conf) ? '0' : Math.round(conf);
            })()}%
          </span>
        </div>
      </div>

      {/* Current Pool Stats */}
      {poolData && (
        <div className="pool-stats">
          <div className="stat">
            <span className="value">{poolData.homePot + poolData.awayPot}</span>
            <span className="label">POOL</span>
          </div>
          <div className="stat">
            <span className="value">{poolData.totalBettors}</span>
            <span className="label">BETTORS</span>
          </div>
          <div className="stat">
            <span className="value">{poolData.homePot}</span>
            <span className="label">FOR {game.home.toUpperCase().substring(0, 3)}</span>
          </div>
          <div className="stat">
            <span className="value">{poolData.awayPot}</span>
            <span className="label">FOR {game.away.toUpperCase().substring(0, 3)}</span>
          </div>
        </div>
      )}

      {/* Betting Options */}
      <div className="betting-options">
        <div className="bet-side-selector">
          <button
            className={`side-btn for ${betSide === 'for' ? 'selected' : ''}`}
            onClick={() => setBetSide('for')}
            disabled={loading || bettingClosed}
          >
            <span className="side-label">FOR</span>
            <span className="team-name">({aiPrediction.consensus})</span>
          </button>
          <button
            className={`side-btn against ${betSide === 'against' ? 'selected' : ''}`}
            onClick={() => setBetSide('against')}
            disabled={loading || bettingClosed}
          >
            <span className="side-label">AGAINST</span>
            <span className="team-name">({getOppositeTeam()})</span>
          </button>
        </div>

        {/* Amount Input */}
        <div className="amount-section">
          <div className="balance-display">
            <span className="balance-label">BALANCE</span>
            <span className="balance-value">{userBalance}</span>
          </div>

          <div className="amount-input-wrapper">
            <input
              type="number"
              className="amount-input"
              placeholder="Enter amount"
              value={betAmount}
              onChange={(e) => setBetAmount(e.target.value)}
              min="1"
              max={userBalance}
              disabled={loading || bettingClosed}
            />
          </div>

          {/* Quick Amounts */}
          <div className="quick-amounts">
            {quickAmounts.map((amount) => (
              <button
                key={amount}
                className={`quick-amount ${betAmount === String(amount) ? 'selected' : ''}`}
                onClick={() => setBetAmount(String(amount))}
                disabled={loading || amount > userBalance || bettingClosed}
              >
                {amount}
              </button>
            ))}
            <button
              className={`quick-amount ${betAmount === String(userBalance) ? 'selected' : ''}`}
              onClick={() => setBetAmount(String(userBalance))}
              disabled={loading || userBalance === 0 || bettingClosed}
            >
              MAX
            </button>
          </div>
        </div>

        {/* Potential Payout */}
        {betAmount && betSide && (
          <div className="payout-info">
            <div className="payout-stat">
              <span className="value">{calculatePotentialPayout()}</span>
              <span className="label">PAYOUT</span>
            </div>
            <div className="payout-stat">
              <span className="value">{getOdds()}</span>
              <span className="label">ODDS</span>
            </div>
            <div className="payout-stat profit">
              <span className="value">+{calculatePotentialPayout() - parseFloat(betAmount || 0)}</span>
              <span className="label">PROFIT</span>
            </div>
          </div>
        )}

        {/* Error/Success Messages */}
        {error && (
          <div className="message error">
            <AlertCircle size={16} />
            {error}
          </div>
        )}

        {success && (
          <div className="message success">
            BET PLACED
          </div>
        )}

        {/* Place Bet Button */}
        <button
          className="place-bet-btn"
          onClick={handlePlaceBet}
          disabled={loading || !betAmount || !betSide || success || bettingClosed}
        >
          {bettingClosed ? 'BETS CLOSED' : loading ? 'Placing Bet...' : 'Place Bet'}
        </button>
      </div>

      {/* Warning */}
      <div className="warning-text">
        <span>All bets final. No cancellations.</span>
      </div>
    </div>
  );
};

// Scaffolding for other sports (commented out)
/**
 * NFL Betting Pool Entry (Future Implementation)
 *
 * const NFLBettingPoolEntry = ({ game, aiPrediction, onClose }) => {
 *   // NFL-specific betting logic
 *   // Different scoring system considerations
 * };
 *
 * NBA Betting Pool Entry (Future Implementation)
 *
 * const NBABettingPoolEntry = ({ game, aiPrediction, onClose }) => {
 *   // NBA-specific betting logic
 *   // Point spread considerations
 * };
 *
 * MLB Betting Pool Entry (Future Implementation)
 *
 * const MLBBettingPoolEntry = ({ game, aiPrediction, onClose }) => {
 *   // MLB-specific betting logic
 *   // Run line considerations
 * };
 */

export default BettingPoolEntry;